import * as zod from 'zod';

import { toEquipmentAssignment } from '@sb/ai/helpers';
import {
  BotHardware,
  HardwareConfigEquipmentMapping,
  HardwareConfigSchema,
} from '@sb/ai/types';
import { ActionRequiredError, Step } from '@sb/remote-control/types';
import { Expression } from '@sb/routine-runner';

/**
 * Types matching the routine runner schema for "CreateRecording" operations.
 */
export namespace CreateRecordingStep {
  export const name = 'Create Recording';
  export const description = 'Record demonstrations for skill training';
  export const isDecorator = true;
  export const librarySection = Step.LibrarySection.Control;
  export const librarySort = '6';
  export const argumentKind = 'CreateRecording';
  export const featureFlag = 'enableLearnedModels';

  // An expression tag with a display name for the UI
  export const TagExpression = zod.object({
    kind: zod.literal('TagExpression'),
    expression: Expression,
    displayName: zod.string(), // The user-friendly name shown in the UI
  });

  export type TagExpression = zod.infer<typeof TagExpression>;

  // A tag can be either a simple string or an expression with a display name
  const TagValue = zod.union([zod.string().max(30), TagExpression]);

  export type TagValue = zod.infer<typeof TagValue>;

  /** Arguments to be passed to the routine runner */
  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    taskId: zod.string().nullable().default(null),
    hardwareConfigEquipmentMapping: HardwareConfigEquipmentMapping.default({}),
    userId: zod.string().nullable().default(null),
    projectId: zod.string().nullable().default(null),
    taskHardwareConfig: zod
      .object({
        id: zod.string(),
        name: zod.string(),
        config: HardwareConfigSchema,
      })
      .nullable()
      .default(null),
    botHardware: zod.record(zod.string(), BotHardware).default({}),
    timeout: zod.number().int().positive().default(300),
    tags: zod.array(TagValue).default([]),
    uploadRecording: zod.boolean().default(true),
    monitorSecondaryBotState: zod.boolean().default(false),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const validator: Step.Validator = ({
    stepConfiguration,
    equipment,
  }) => {
    const args = stepConfiguration?.args;

    if (args?.argumentKind !== argumentKind) {
      return;
    }

    if (!args.userId || args.userId.trim() === '') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'User ID is required.',
        fieldId: 'userId',
      });
    }

    if (!args.taskId || args.taskId.trim() === '') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Task ID is required.',
        fieldId: 'taskId',
      });
    }

    if (args.taskHardwareConfig) {
      const { config } = args.taskHardwareConfig;
      const { hardwareConfigEquipmentMapping } = args;

      for (const [robotKey, robot] of Object.entries(config.robots)) {
        const mapping = hardwareConfigEquipmentMapping[robotKey] ?? {
          hardwareKeyToEquipmentId: {},
        };

        if (!robot.isPrimary) {
          if (!mapping.robotEquipmentId) {
            throw new ActionRequiredError({
              kind: 'invalidConfiguration',
              message: `Equipment not assigned for ${robotKey}`,
              fieldId: 'hardwareConfigEquipmentMapping',
            });
          }

          if (
            equipment &&
            !equipment.find((e) => e.id === mapping.robotEquipmentId)
          ) {
            throw new ActionRequiredError({
              kind: 'invalidConfiguration',
              message: `Selected equipment for ${robotKey} is not available`,
              fieldId: 'hardwareConfigEquipmentMapping',
            });
          }
        }

        for (const [hardwareKey, hardwareConfig] of Object.entries(
          robot.hardware,
        )) {
          const hardware = args.botHardware?.[hardwareConfig.id];
          const rawAssignment = mapping.hardwareKeyToEquipmentId[hardwareKey];

          const equipmentId =
            rawAssignment != null
              ? toEquipmentAssignment(rawAssignment).equipmentId
              : undefined;

          if (!equipmentId && hardware?.equipmentType !== 'robot') {
            throw new ActionRequiredError({
              kind: 'invalidConfiguration',
              message: `Equipment not assigned for hardware key ${hardwareKey}`,
              fieldId: 'hardwareConfigEquipmentMapping',
            });
          }

          if (
            robot.isPrimary && // For now, don't try to validate secondary equipment as that would require secondary equipment state
            equipmentId &&
            equipment &&
            !equipment.find((e) => e.id === equipmentId)
          ) {
            throw new ActionRequiredError({
              kind: 'invalidConfiguration',
              message: `Selected equipment for ${hardwareKey} is not available`,
              fieldId: 'hardwareConfigEquipmentMapping',
            });
          }
        }
      }
    }
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'CreateRecording',
      args: {
        taskId: args.taskId,
        hardwareConfigEquipmentMapping: args.hardwareConfigEquipmentMapping,
        userId: args.userId,
        projectId: args.projectId,
        taskHardwareConfig: args.taskHardwareConfig,
        botHardware: args.botHardware,
        timeout: args.timeout,
        tags: args.tags,
        uploadRecording: args.uploadRecording,
        monitorSecondaryBotState: args.monitorSecondaryBotState,
      },
    };
  };
}

CreateRecordingStep satisfies Step.StepKindInfo;
